import { useContext, useEffect, useRef, useState } from "react";
import { AppContext } from "../App";
import { getInlineLoader } from "../Helpers";
import OneGallery from "./OneGallery";
import { FaArrowRight } from "react-icons/fa6";

/* ------------------------------------------------------------------ */
/* Scroll-reveal utility                                              */
/* ------------------------------------------------------------------ */
function useReveal(options = {}) {
  const ref = useRef(null);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const el = ref.current;
    if (!el) return;
    const io = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setVisible(true);
          io.disconnect();
        }
      },
      { threshold: 0.1, rootMargin: "0px 0px -40px 0px", ...options }
    );
    io.observe(el);
    return () => io.disconnect();
  }, []);

  return [ref, visible];
}

function Reveal({ as: Tag = "div", children, delay = 0, className = "" }) {
  const [ref, visible] = useReveal();
  return (
    <Tag
      ref={ref}
      className={`reveal ${visible ? "is-visible" : ""} ${className}`}
      style={{ transitionDelay: `${delay}ms` }}
    >
      {children}
    </Tag>
  );
}

/* ------------------------------------------------------------------ */
/* Page                                                               */
/* ------------------------------------------------------------------ */
export default function GalleryBanner(props) {
  const appContext = useContext(AppContext);
  const [ready, setReady] = useState(false);

  async function init() {
    setReady(false);
    await Promise.all([
      appContext.getAllGallery(),
    ]);
    setReady(true);
  }

  useEffect(() => {
    init();
  }, []);

  if (!ready) {
    return (
      <div className="container py-5 d-flex justify-content-center" style={{ backgroundColor: '#0f172a', minHeight: '50vh', alignItems: 'center' }}>
        {getInlineLoader()}
      </div>
    );
  }

  const allGallery = Array.isArray(appContext.allGallery) ? appContext.allGallery : [];
  
  // Routing Logic Check (Using 'gallery' route string)
  const isGalleryRoute = appContext.item === 'gallery';
  const displayGallery = isGalleryRoute ? allGallery : allGallery.slice(0, 3);

  return (
    <section className="mod-gallery-section">
      <style>{`
        /* --- Base & Dark Theme --- */
        .mod-gallery-section {
          background-color: #ffffff; /* Deep Navy Slate */
          color: #f8fafc;
          font-family: system-ui, -apple-system, sans-serif;
          padding: 6rem 0;
          position: relative;
          min-height: 50vh;
        }

        /* --- Scroll Reveal Animations --- */
        .reveal {
          opacity: 0;
          transform: translateY(30px);
          transition: all 0.8s cubic-bezier(0.2, 0.8, 0.2, 1);
        }
        .reveal.is-visible {
          opacity: 1;
          transform: translateY(0);
        }

        /* --- Typography --- */
        .mod-kicker {
          font-size: 0.85rem;
          text-transform: uppercase;
          letter-spacing: 0.15em;
          color: #60a5fa;
          font-weight: 600;
          margin-bottom: 0.5rem;
          display: block;
        }
        .mod-title {
          font-size: clamp(2rem, 4vw, 2.75rem);
          font-weight: 700;
          color: var(--primaryColor);
          margin-bottom: 3rem;
          line-height: 1.2;
        }

        /* --- Dynamic Gallery Wrapping for dark theme --- */
        .gallery-dynamic-wrap {
          height: 100%;
          transition: transform 0.3s ease;
        }
        .gallery-dynamic-wrap:hover {
          transform: translateY(-4px);
        }
        /* Forcing dark mode properties on internal OneGallery components if they use standard bootstrap cards */
        .gallery-dynamic-wrap .card, 
        .gallery-dynamic-wrap .bg-white,
        .gallery-dynamic-wrap .bg-light {
          background: rgba(30, 41, 59, 0.5) !important;
          border: 1px solid rgba(255, 255, 255, 0.08) !important;
          color: #f8fafc !important;
          backdrop-filter: blur(10px);
          border-radius: 12px;
          overflow: hidden;
        }
        .gallery-dynamic-wrap .text-dark, 
        .gallery-dynamic-wrap .text-muted {
          color: #94a3b8 !important;
        }

        .mod-empty-state {
          background: rgba(30, 41, 59, 0.3);
          border: 1px dashed rgba(255,255,255,0.1);
          border-radius: 16px;
          padding: 3rem;
          color: #64748b;
        }

        /* --- Buttons --- */
        .mod-btn {
          display: inline-flex;
          align-items: center;
          justify-content: center;
          gap: 8px;
          padding: 14px 32px;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 600;
          transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
          cursor: pointer;
          border: none;
        }
        .mod-btn-outline {
          background-color: transparent;
          color: white;
          border: 1px solid rgba(255, 255, 255, 0.3);
          backdrop-filter: blur(5px);
        }
        .mod-btn-outline:hover {
          background-color: rgba(255, 255, 255, 0.1);
          border-color: rgba(255, 255, 255, 0.8);
          transform: translateY(-2px);
        }
      `}</style>

      <div className="container">
        
        {/* --- SECTION HEADER --- */}
        <div className="text-center mb-5">
          <Reveal>
            <span className="mod-kicker">Visuals</span>
            <h2 className="mod-title mb-2 text-primary">Our Gallery</h2>
            <p style={{ color: '#94a3b8', maxWidth: '600px', margin: '0 auto' }}>
              A visual record of our projects, operations, and the impact we make across the region.
            </p>
          </Reveal>
        </div>

        {/* --- GALLERY GRID --- */}
        <div className="row g-4 justify-content-center">
          {displayGallery.length > 0 ? (
            displayGallery.map((item, i) => (
              <div key={i} className="col-12 col-md-6 col-lg-4">
                <Reveal delay={i * 100} className="gallery-dynamic-wrap">
                  <OneGallery data={item} />
                </Reveal>
              </div>
            ))
          ) : (
            <div className="col-12">
              <Reveal className="mod-empty-state text-center">
                <p className="mb-0 fst-italic">No gallery visuals available at this time.</p>
              </Reveal>
            </div>
          )}
        </div>

        {/* --- CONDITIONAL CTA: VIEW ALL GALLERY --- */}
        {!isGalleryRoute && allGallery.length > 3 && (
          <Reveal delay={200}>
            <div className="text-center mt-5 pt-3">
              <button
                className="mod-btn mod-btn-outline"
                onClick={() => appContext.navTo({ item: "gallery" })}
              >
                View Full Gallery <FaArrowRight />
              </button>
            </div>
          </Reveal>
        )}

      </div>
    </section>
  );
}